import { useEffect, useState, type FormEvent } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { YkkbiLogoImage } from "../components/Brand.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { useChangePasswordMutation } from "../store/api.js";
import { credentialsReceived, loggedOut } from "../store/auth.slice.js";
import { useAppDispatch, useAppSelector } from "../store/index.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MIN_LENGTH = 8;

export function ChangePasswordPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { accessToken, user } = useAppSelector((state) => state.auth);
  const [changePassword, { isLoading, error }] = useChangePasswordMutation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [localError, setLocalError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = "Ubah Kata Sandi · YKKBI";
  }, []);

  if (!accessToken || !user) return <Navigate to="/login" replace />;

  const forced = user.mustChangePassword;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (newPassword.length < MIN_LENGTH) {
      setLocalError(`Kata sandi baru minimal ${MIN_LENGTH} karakter.`);
      return;
    }
    if (newPassword !== confirmation) {
      setLocalError("Kedua kata sandi baru tidak sama.");
      return;
    }
    if (newPassword === currentPassword) {
      setLocalError("Pilih kata sandi yang belum pernah dipakai pada akun ini.");
      return;
    }
    setLocalError("");

    const result = await changePassword({ currentPassword, newPassword });
    if (result.error) return;
    dispatch(credentialsReceived(result.data));
    navigate("/", { replace: true });
  }

  return (
    <div className="relative min-h-screen w-full bg-[#FAFAFA] flex flex-col justify-between p-4 sm:p-6 overflow-hidden">
      {/* Background Dot Grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage: "radial-gradient(#101828 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="w-full" />

      {/* Main Centered Card */}
      <div className="relative z-10 mx-auto w-full max-w-[440px]">
        <div className="rounded-2xl border border-[#EAECF0] bg-white p-7 sm:p-8 shadow-xl">
          {/* Header */}
          <div className="flex flex-col items-center text-center">
            <YkkbiLogoImage size="lg" className="shadow-sm" />
            <h1 className="mt-4 text-xl font-bold tracking-tight text-[#101828]">
              Ubah Kata Sandi
            </h1>
            <p className="mt-1 text-xs text-[#667085]">
              {forced ? (
                <span className="text-[#B54708] font-medium">
                  Akun Anda memerlukan pembaruan kata sandi awal sebelum dapat mengakses konsol.
                </span>
              ) : (
                `Masuk sebagai @${user.username}`
              )}
            </p>
          </div>

          {(localError || error) && (
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-[#FECDCA] bg-[#FEF3F2] p-3 text-xs text-[#B42318]">
              <AlertTriangle className="size-4 shrink-0" />
              <span>{localError || apiErrorMessage(error)}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[#344054]">
                Kata sandi saat ini
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  className="h-10 text-xs pr-10 border-[#D0D5DD] focus-visible:ring-1 focus-visible:ring-[#2563EB]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#98A2B3] hover:text-[#344054]"
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[#344054]">
                Kata sandi baru (minimal 8 karakter)
              </Label>
              <Input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
                minLength={MIN_LENGTH}
                className="h-10 text-xs border-[#D0D5DD] focus-visible:ring-1 focus-visible:ring-[#2563EB]"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[#344054]">
                Ulangi kata sandi baru
              </Label>
              <Input
                type={showPassword ? "text" : "password"}
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
                minLength={MIN_LENGTH}
                className="h-10 text-xs border-[#D0D5DD] focus-visible:ring-1 focus-visible:ring-[#2563EB]"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="mt-2 w-full h-10 gap-2 bg-[#0F1B3D] text-white hover:bg-[#1D2939] text-xs font-semibold shadow-xs"
            >
              {isLoading ? "Menyimpan…" : "Simpan dan Masuk"}
              <ArrowRight className="size-4" />
            </Button>
          </form>

          {/* Sign out fallback */}
          <div className="mt-4 pt-4 border-t border-[#EAECF0] text-center">
            <button
              type="button"
              onClick={() => {
                dispatch(loggedOut());
                navigate("/login", { replace: true });
              }}
              className="inline-flex items-center gap-1.5 text-xs text-[#667085] hover:text-[#B42318] transition-colors"
            >
              <LogOut className="size-3.5" />
              Keluar dan kembali ke halaman login
            </button>
          </div>
        </div>

        {/* Security Footer Note */}
        <div className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-[#667085]">
          <Lock className="size-3.5 text-[#98A2B3]" />
          <span>Koneksi aman terenkripsi TLS 1.3 · Bank Indonesia</span>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 text-center text-xs text-[#98A2B3] py-4">
        © 2026 Yayasan Kesejahteraan Karyawan Bank Indonesia. Hak Cipta Dilindungi.
      </footer>
    </div>
  );
}
